/**
 * Create/edit dialog for a Dashplan block.
 *
 * Local form state is seeded from the entry and the dialog is keyed on the
 * entry id by its parent, so switching blocks remounts with fresh state rather
 * than showing the previous block's half-edited values. Convex re-emits a new
 * object graph on every update, so holding the entry itself in state and
 * comparing by identity would go stale within seconds.
 */
import { useState } from "react";
import { useQuery } from "convex/react";
import { api } from "@convex/_generated/api";
import type { Id } from "@convex/_generated/dataModel";
import { Trash2, Link2Off } from "lucide-react";

import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { TimeField } from "@/components/time-field";
import { formatMinute, formatEndMinute, formatDuration } from "@/lib/dates";
import { KIND_ORDER, KINDS, DURATION_PRESETS, type DashplanKind } from "./constants";
import type { TimelineEntry } from "./block";

export interface BlockDraft {
  title: string;
  notes: string;
  kind: DashplanKind;
  startMinute: number;
  durationMinutes: number;
  location: string;
  linkedTaskId: Id<"tasks"> | null;
  linkedGoalId: Id<"goals"> | null;
}

export function blockDraftFrom(entry: TimelineEntry): BlockDraft {
  return {
    title: entry.title,
    notes: entry.notes ?? "",
    kind: entry.kind as DashplanKind,
    startMinute: entry.startMinute,
    durationMinutes: entry.durationMinutes,
    location: entry.location ?? "",
    linkedTaskId: entry.linkedTaskId ?? null,
    linkedGoalId: entry.linkedGoalId ?? null,
  };
}

export function emptyDraft(startMinute: number): BlockDraft {
  return {
    title: "",
    notes: "",
    kind: "other",
    startMinute,
    durationMinutes: 30,
    location: "",
    linkedTaskId: null,
    linkedGoalId: null,
  };
}

/** Sentinel for the Select's "not linked" option — Radix rejects "". */
const NONE = "__none__";

interface BlockEditorProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initial: BlockDraft;
  /** Present when editing; absent when creating. */
  entryId?: Id<"dashplanEntries">;
  onSave: (draft: BlockDraft) => Promise<void> | void;
  onDelete?: () => Promise<void> | void;
}

export function BlockEditor({
  open,
  onOpenChange,
  initial,
  entryId,
  onSave,
  onDelete,
}: BlockEditorProps) {
  const [draft, setDraft] = useState<BlockDraft>(initial);
  const [saving, setSaving] = useState(false);

  const tasks = useQuery(api.tasks.list, { status: "pending" });
  const goals = useQuery(api.quests.openGoals, { limit: 25 });

  const set = <K extends keyof BlockDraft>(key: K, value: BlockDraft[K]) =>
    setDraft((current) => ({ ...current, [key]: value }));

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!draft.title.trim()) return;

    setSaving(true);
    try {
      await onSave(draft);
      onOpenChange(false);
    } finally {
      setSaving(false);
    }
  };

  const endMinute = draft.startMinute + draft.durationMinutes;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[90dvh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{entryId ? "Edit block" : "New block"}</DialogTitle>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="block-title">What</Label>
            <Input
              id="block-title"
              value={draft.title}
              onChange={(e) => set("title", e.target.value)}
              placeholder="Meeting with Kari"
              autoFocus
              required
              maxLength={200}
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="block-time">Starts</Label>
              <TimeField
                id="block-time"
                value={draft.startMinute}
                onChange={(minute) => set("startMinute", minute)}
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="block-duration">For</Label>
              <Select
                value={String(draft.durationMinutes)}
                onValueChange={(value) => set("durationMinutes", Number(value))}
              >
                <SelectTrigger id="block-duration">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {/* Include the current value even when it is not a preset,
                      so an odd duration is never silently rewritten. */}
                  {[
                    ...new Set([...DURATION_PRESETS, draft.durationMinutes]),
                  ]
                    .sort((a, b) => a - b)
                    .map((minutes) => (
                      <SelectItem key={minutes} value={String(minutes)}>
                        {formatDuration(minutes)}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <p className="text-xs text-muted-foreground font-mono tabular-nums">
            {formatMinute(draft.startMinute)} – {formatEndMinute(endMinute)}
          </p>

          <div className="space-y-1.5">
            <Label htmlFor="block-kind">Kind</Label>
            <Select
              value={draft.kind}
              onValueChange={(value) => set("kind", value as DashplanKind)}
            >
              <SelectTrigger id="block-kind">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {KIND_ORDER.map((kind) => (
                  <SelectItem key={kind} value={kind}>
                    {KINDS[kind].label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="block-location">Where (optional)</Label>
            <Input
              id="block-location"
              value={draft.location}
              onChange={(e) => set("location", e.target.value)}
              placeholder="Hytta, Meeting room 3, …"
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="block-task">Linked task (optional)</Label>
            <Select
              value={draft.linkedTaskId ?? NONE}
              onValueChange={(value) =>
                set(
                  "linkedTaskId",
                  value === NONE ? null : (value as Id<"tasks">),
                )
              }
            >
              <SelectTrigger id="block-task">
                <SelectValue placeholder="Not linked" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NONE}>
                  <span className="flex items-center gap-2 text-muted-foreground">
                    <Link2Off className="w-3.5 h-3.5" />
                    Not linked
                  </span>
                </SelectItem>
                {(tasks ?? []).map((task) => (
                  <SelectItem key={task._id} value={task._id}>
                    {task.title}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              Marking this block done also completes the linked task.
            </p>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="block-goal">Linked goal (optional)</Label>
            <Select
              value={draft.linkedGoalId ?? NONE}
              onValueChange={(value) =>
                set(
                  "linkedGoalId",
                  value === NONE ? null : (value as Id<"goals">),
                )
              }
            >
              <SelectTrigger id="block-goal">
                <SelectValue placeholder="Not linked" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NONE}>
                  <span className="flex items-center gap-2 text-muted-foreground">
                    <Link2Off className="w-3.5 h-3.5" />
                    Not linked
                  </span>
                </SelectItem>
                {(goals ?? []).map((goal) => (
                  <SelectItem key={goal._id} value={goal._id}>
                    {goal.title}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="block-notes">Notes (optional)</Label>
            <Textarea
              id="block-notes"
              value={draft.notes}
              onChange={(e) => set("notes", e.target.value)}
              rows={3}
              placeholder="Agenda, what to bring, who to call…"
            />
          </div>

          <DialogFooter className="gap-2 sm:gap-2">
            {onDelete && (
              <Button
                type="button"
                variant="ghost"
                className="mr-auto text-destructive hover:text-destructive"
                onClick={async () => {
                  await onDelete();
                  onOpenChange(false);
                }}
              >
                <Trash2 className="w-4 h-4 mr-1.5" />
                Delete
              </Button>
            )}
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={saving || !draft.title.trim()}>
              {saving ? "Saving…" : entryId ? "Save" : "Add block"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
